import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { setShowPartialPaymentPopup } from '../redux/slices/feesSlice';
import { RootState } from '../redux/root';
import { currencyFormat } from '../pages/Permits/FeesPage';
import { ButtonMain, Loader } from '../atoms';
import { getPermitsFeesStatistics, postPartialPayment } from '../api/fees';
import { setPulse } from '../redux/slices/generalSlice';

const SubPermitMenuPartialPayment = () => {
  const dispatch = useDispatch();
  const permitFeesToManualyPay = useSelector((s: RootState) => s.fees.permitFeesToManualyPay);
  const pulse = useSelector((s: RootState) => s.general.pulse);
  const [balance, setBalance] = useState<number>(0);
  const [orig, setOrig] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  // EA-1402: the payment can take a few seconds to distribute across invoices.
  // `isProcessing` disables the button and shows a loading state during that
  // window. The ref is a synchronous re-entry guard: React state updates are
  // async, so two rapid clicks could both pass a state-only check before the
  // first re-render — the ref blocks the second click immediately.
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const isPayingRef = useRef<boolean>(false);
  // @ts-ignore
  const { permitId } = useParams();

  useEffect(() => {
    const load = async () => {
      const result = await getPermitsFeesStatistics({
        permit: permitId,
      });

      if (result && result.data) {
        // @ts-ignore
        const total = result.data.outstanding;
        setBalance(total);
        setOrig(total);
      }
    };
    if (permitId) {
      load();
    }
  }, [permitId]);

  const onChange = e => {
    const val = e.target.value;
    let _error = false;
    if (!isNaN(val)) {
      setAmount(val);
      const bal = orig - Number(val);
      setBalance(bal);
    }
    if (Number(val) > orig) {
      _error = true;
      setBalance(orig);
    }

    if (val === '') {
      setBalance(orig);
    }
    setError(_error);
  };

  const isPayDisabled = () => {
    // EA-1402: block any further submissions once a payment is in flight.
    if (isProcessing) {
      return true;
    }

    if (!amount || !memo) {
      return true;
    }

    if (Number(amount) > orig) {
      return true;
    }
    return false;
  };

  const onPay = async () => {
    // EA-1402: synchronous re-entry guard. Prevents duplicate payment records
    // from rapid double-clicks while the request is still processing, before
    // the disabled state has had a chance to re-render.
    if (isPayingRef.current) {
      return;
    }
    isPayingRef.current = true;
    setIsProcessing(true);

    const invoiceIds = permitFeesToManualyPay
      .filter(p => p.invoiceNumber.startsWith('INV'))
      .reduce((ids, fee) => {
        if (ids.indexOf(fee.invoiceId) === -1) {
          ids.push(fee.invoiceId);
        }
        return ids;
      }, []);
    const feeIds = permitFeesToManualyPay.map(p => p.id);
    const payload = {
      payment_amount: amount,
      memo,
      invoices: invoiceIds,
      permit: permitId,
      fee_id: feeIds.length > 0 ? feeIds : undefined,
    };
    try {
      await postPartialPayment(payload);
      dispatch(setPulse(pulse + 1));
      onClose();
    } catch (e) {
      // On failure, re-enable so the user can retry (the request error itself
      // is surfaced by the global error modal, as elsewhere in the payment
      // flow). The popup stays open with the entered amount/memo intact.
      isPayingRef.current = false;
      setIsProcessing(false);
    }
  };

  const onClose = () => {
    dispatch(setShowPartialPaymentPopup(false));
  };

  return (
    <Wrapper>
      <PopupCard>
        <CardHeader>
          <CardTitle>Accept Payment</CardTitle>
          <CloseBtn onClick={onClose} aria-label="Close">
            <CloseIcon />
          </CloseBtn>
        </CardHeader>

        <BalanceBadge>
          <BalanceLabel>Outstanding Amount</BalanceLabel>
          <BalanceValue>{currencyFormat.format(balance)}</BalanceValue>
        </BalanceBadge>

        <FormBody>
          <FieldRow>
            <FieldLabel>
              Payment Amount<RequiredStar>*</RequiredStar>
            </FieldLabel>
            <StyledInput
              type="text"
              value={amount}
              name="amount"
              onChange={onChange}
              placeholder="0.00"
              $error={error}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              Payment Memo<RequiredStar>*</RequiredStar>
            </FieldLabel>
            <StyledInput
              type="text"
              value={memo}
              name="memo"
              onChange={e => setMemo(e.target.value)}
              placeholder="Enter memo..."
              $wide
            />
          </FieldRow>
          {error && <ErrorMsg>Amount cannot exceed the outstanding balance</ErrorMsg>}
        </FormBody>

        <PayBtn
          color="primary"
          disabled={isPayDisabled()}
          onClick={onPay}
          startIcon={
            isProcessing ? (
              <Loader size={18} thickness={5} />
            ) : undefined
          }
        >
          {isProcessing ? 'Processing…' : 'Pay Now'}
        </PayBtn>
      </PopupCard>
    </Wrapper>
  );
};

export default SubPermitMenuPartialPayment;

const Wrapper = styled.div`
  position: relative;
`;

const PopupCard = styled.div`
  position: absolute;
  top: 52px;
  right: 0;
  width: 420px;
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: 3;
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  color: var(--neutral-900);
  margin: 0;
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: var(--radius-full);
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  svg { width: 20px; height: 20px; }
  &:hover { background: var(--neutral-100); color: var(--neutral-800); }
`;

const BalanceBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-3);
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: var(--radius-md);
`;

const BalanceLabel = styled.div`
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--primary-600);
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const BalanceValue = styled.div`
  font-size: var(--fs-2xl);
  font-weight: var(--fw-bold);
  color: var(--primary-800);
`;

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const FieldLabel = styled.label`
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--neutral-700);
`;

// EA-936: mandatory-field indicator on Payment Amount / Payment Memo.
const RequiredStar = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const StyledInput = styled.input<{ $error?: boolean; $wide?: boolean }>`
  width: 100%;
  height: 40px;
  padding: 0 var(--space-3);
  border: 1px solid ${({ $error }) => $error ? 'var(--danger-500)' : 'var(--neutral-300)'};
  border-radius: var(--radius-md);
  font-size: var(--fs-base);
  font-family: inherit;
  color: var(--neutral-800);
  background: var(--neutral-0);
  transition: border-color var(--motion-fast) var(--ease-standard),
    box-shadow var(--motion-fast) var(--ease-standard);

  &::placeholder { color: var(--neutral-400); }
  &:focus {
    outline: none;
    border-color: ${({ $error }) => $error ? 'var(--danger-500)' : 'var(--primary-600)'};
    box-shadow: ${({ $error }) => $error ? 'var(--shadow-focus-danger)' : 'var(--shadow-focus)'};
  }
`;

const ErrorMsg = styled.div`
  font-size: var(--fs-sm);
  color: var(--danger-500);
`;

const PayBtn = styled(ButtonMain)`
  width: 100%;
  min-height: 44px;
  font-size: var(--fs-lg);
  font-weight: var(--fw-semibold);
`;
